import React, { useState, useEffect, memo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { chiusureApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  CalendarDays, 
  Sun, 
  Moon, 
  Users, 
  Banknote, 
  CreditCard,
  Building2,
  FileText,
  Ticket,
  Save,
  Loader2,
  Receipt,
  Cloud,
  Calculator,
  XCircle,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '../lib/utils';

const GIORNI = ['DOM', 'LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB'];

// Componente NumberInput definito FUORI dal componente principale
// per evitare la ricreazione ad ogni render che causa la perdita del focus
const NumberInput = memo(({ icon: Icon, label, field, placeholder = "0", isCurrency = false, value, onChange }) => {
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    
    // Allow empty string
    if (newValue === '') {
      onChange(field, '');
      return;
    }
    
    // For currency fields, allow decimal numbers with comma or point
    // For integer fields (coperti), only allow whole numbers
    const regex = isCurrency ? /^[0-9]*[.,]?[0-9]*$/ : /^[0-9]*$/;
    
    if (regex.test(newValue)) {
      // Convert comma to point for consistency
      const normalizedValue = newValue.replace(',', '.');
      onChange(field, normalizedValue);
    }
  };

  // Display value: show empty string if value is empty, "0", or 0
  const displayValue = value === '' || value === '0' || value === 0 ? '' : value;

  return (
    <div className="space-y-2">
      <Label className="text-sm text-muted-foreground flex items-center gap-2">
        <Icon className="w-4 h-4" />
        {label}
      </Label>
      <div className="relative">
        {isCurrency && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono-numbers">€</span>
        )}
        <Input
          data-testid={`input-${field}`}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder={placeholder}
          value={displayValue}
          onChange={handleChange}
          className={cn(
            "h-14 font-mono-numbers text-lg",
            isCurrency && "pl-8"
          )}
        />
      </div>
    </div>
  );
});

NumberInput.displayName = 'NumberInput';

const NuovaChiusuraPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const completaId = searchParams.get('completa'); // ID della chiusura parziale da completare
  
  const isEditing = !!id;
  const isCompletingPartial = !!completaId;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(new Date());
  const [giornoSettimana, setGiornoSettimana] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [chiusuraOriginale, setChiusuraOriginale] = useState(null); // Per memorizzare la chiusura parziale
  
  // Checkbox per locale chiuso
  const [chiusoPranzo, setChiusoPranzo] = useState(false);
  const [chiusoCena, setChiusoCena] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Pranzo
    coperti_pranzo: '',
    contanti_pranzo: '',
    pos_pranzo: '',
    bonifico_pranzo: '',
    assegno_pranzo: '',
    buoni_pasto_pranzo: '',
    note_pranzo: '',
    // Cena
    coperti_cena: '',
    contanti_cena: '',
    pos_cena: '',
    bonifico_cena: '',
    assegno_cena: '',
    buoni_pasto_cena: '',
    note_cena: '',
    // Riepilogo Fiscale
    totale_scontrini: '',
    totale_fatture: '',
    totale_cassa_in_cloud: '',
  });

  // Calculated fields for live preview
  const [calculatedFields, setCalculatedFields] = useState({
    incasso_giorno: 0,
    proforma: 0,
    delta_cassa_cloud_vs_incasso: 0,
  });

  useEffect(() => {
    updateGiornoSettimana(date);
    if (isEditing) {
      loadChiusura();
    } else if (isCompletingPartial) {
      loadChiusuraParziale();
    }
  }, [id, completaId]);

  // Calculate derived fields whenever form data changes
  useEffect(() => {
    const incasso_pranzo = 
      (parseFloat(formData.contanti_pranzo) || 0) +
      (parseFloat(formData.pos_pranzo) || 0) +
      (parseFloat(formData.bonifico_pranzo) || 0) +
      (parseFloat(formData.assegno_pranzo) || 0) +
      (parseFloat(formData.buoni_pasto_pranzo) || 0);

    const incasso_cena = 
      (parseFloat(formData.contanti_cena) || 0) +
      (parseFloat(formData.pos_cena) || 0) +
      (parseFloat(formData.bonifico_cena) || 0) +
      (parseFloat(formData.assegno_cena) || 0) +
      (parseFloat(formData.buoni_pasto_cena) || 0);

    const incasso_giorno = incasso_pranzo + incasso_cena;

    const totale_scontrini = parseFloat(formData.totale_scontrini) || 0;
    const totale_fatture = parseFloat(formData.totale_fatture) || 0;
    const totale_cassa_in_cloud = parseFloat(formData.totale_cassa_in_cloud) || 0;

    const proforma = totale_cassa_in_cloud - totale_scontrini - totale_fatture;
    const delta_cassa_cloud_vs_incasso = totale_cassa_in_cloud - incasso_giorno;

    setCalculatedFields({
      incasso_giorno: Math.round(incasso_giorno * 100) / 100,
      proforma: Math.round(proforma * 100) / 100,
      delta_cassa_cloud_vs_incasso: Math.round(delta_cassa_cloud_vs_incasso * 100) / 100,
    });
  }, [formData]);

  const loadChiusuraParziale = async () => {
    setLoading(true);
    try {
      const data = await chiusureApi.getById(completaId);
      setChiusuraOriginale(data);
      setDate(new Date(data.data_servizio));
      setGiornoSettimana(data.giorno_settimana);
      // Pre-compila solo i dati del pranzo dalla chiusura parziale
      setFormData({
        coperti_pranzo: data.coperti_pranzo.toString(),
        contanti_pranzo: data.contanti_pranzo.toString(),
        pos_pranzo: data.pos_pranzo.toString(),
        bonifico_pranzo: data.bonifico_pranzo.toString(),
        assegno_pranzo: data.assegno_pranzo.toString(),
        buoni_pasto_pranzo: data.buoni_pasto_pranzo.toString(),
        note_pranzo: data.note_pranzo || '',
        // Cena vuota - da completare
        coperti_cena: '',
        contanti_cena: '',
        pos_cena: '',
        bonifico_cena: '',
        assegno_cena: '',
        buoni_pasto_cena: '',
        note_cena: '',
        // Fiscale vuoto - da completare
        totale_scontrini: '',
        totale_fatture: '',
        totale_cassa_in_cloud: '',
      });
    } catch (error) {
      toast.error('Errore nel caricamento della chiusura');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadChiusura = async () => {
    setLoading(true);
    try {
      const data = await chiusureApi.getById(id);
      setDate(new Date(data.data_servizio));
      setGiornoSettimana(data.giorno_settimana);
      setFormData({
        coperti_pranzo: data.coperti_pranzo.toString(),
        contanti_pranzo: data.contanti_pranzo.toString(),
        pos_pranzo: data.pos_pranzo.toString(),
        bonifico_pranzo: data.bonifico_pranzo.toString(),
        assegno_pranzo: data.assegno_pranzo.toString(),
        buoni_pasto_pranzo: data.buoni_pasto_pranzo.toString(),
        note_pranzo: data.note_pranzo || '',
        coperti_cena: data.coperti_cena.toString(),
        contanti_cena: data.contanti_cena.toString(),
        pos_cena: data.pos_cena.toString(),
        bonifico_cena: data.bonifico_cena.toString(),
        assegno_cena: data.assegno_cena.toString(),
        buoni_pasto_cena: data.buoni_pasto_cena.toString(),
        note_cena: data.note_cena || '',
        totale_scontrini: (data.totale_scontrini || 0).toString(),
        totale_fatture: (data.totale_fatture || 0).toString(),
        totale_cassa_in_cloud: (data.totale_cassa_in_cloud || 0).toString(),
      });
    } catch (error) {
      toast.error('Errore nel caricamento');
      navigate('/chiusure');
    } finally {
      setLoading(false);
    }
  };

  const updateGiornoSettimana = (selectedDate) => {
    const giorno = GIORNI[selectedDate.getDay()];
    setGiornoSettimana(giorno);
  };

  const handleDateSelect = (selectedDate) => {
    if (selectedDate) {
      setDate(selectedDate);
      updateGiornoSettimana(selectedDate);
      setCalendarOpen(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Gestione checkbox "Chiuso"
  const handleChiusoPranzo = (checked) => {
    setChiusoPranzo(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        coperti_pranzo: '0',
        contanti_pranzo: '0',
        pos_pranzo: '0',
        bonifico_pranzo: '0',
        assegno_pranzo: '0',
        buoni_pasto_pranzo: '0',
        note_pranzo: 'Chiuso a pranzo',
      }));
    }
  };

  const handleChiusoCena = (checked) => {
    setChiusoCena(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        coperti_cena: '0',
        contanti_cena: '0',
        pos_cena: '0',
        bonifico_cena: '0',
        assegno_cena: '0',
        buoni_pasto_cena: '0',
        note_cena: 'Chiuso a cena',
      }));
    }
  };

  // Validazione form
  const validateForm = (isParziale = false) => {
    // Per chiusura parziale (solo pranzo), validazione più leggera
    if (isParziale) {
      const hasPranzoData = formData.coperti_pranzo || formData.contanti_pranzo || formData.pos_pranzo;
      if (!hasPranzoData && !chiusoPranzo) {
        toast.error('Compila i dati del pranzo');
        return false;
      }
      return true;
    }
    
    // Per chiusura completa
    // Verifica parte fiscale
    if (!chiusoPranzo && !chiusoCena) {
      // Almeno uno tra pranzo e cena deve avere dati o essere marcato come chiuso
      const hasPranzoData = formData.coperti_pranzo || formData.contanti_pranzo || formData.pos_pranzo;
      const hasCenaData = formData.coperti_cena || formData.contanti_cena || formData.pos_cena;
      
      if (!hasPranzoData && !hasCenaData) {
        toast.error('Compila almeno i dati del pranzo o della cena');
        return false;
      }
    }
    
    // Verifica totale cassa in cloud (obbligatorio per parte fiscale)
    if (!formData.totale_cassa_in_cloud && formData.totale_cassa_in_cloud !== '0') {
      toast.error('Il Totale Cassa in Cloud è obbligatorio');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (isParziale = false) => {
    if (!validateForm(isParziale)) {
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        data_servizio: format(date, 'yyyy-MM-dd'),
        stato: isParziale ? 'parziale' : 'completa',
        coperti_pranzo: parseInt(formData.coperti_pranzo) || 0,
        contanti_pranzo: parseFloat(formData.contanti_pranzo) || 0,
        pos_pranzo: parseFloat(formData.pos_pranzo) || 0,
        bonifico_pranzo: parseFloat(formData.bonifico_pranzo) || 0,
        assegno_pranzo: parseFloat(formData.assegno_pranzo) || 0,
        buoni_pasto_pranzo: parseFloat(formData.buoni_pasto_pranzo) || 0,
        note_pranzo: formData.note_pranzo,
        coperti_cena: parseInt(formData.coperti_cena) || 0,
        contanti_cena: parseFloat(formData.contanti_cena) || 0,
        pos_cena: parseFloat(formData.pos_cena) || 0,
        bonifico_cena: parseFloat(formData.bonifico_cena) || 0,
        assegno_cena: parseFloat(formData.assegno_cena) || 0,
        buoni_pasto_cena: parseFloat(formData.buoni_pasto_cena) || 0,
        note_cena: formData.note_cena,
        totale_scontrini: parseFloat(formData.totale_scontrini) || 0,
        totale_fatture: parseFloat(formData.totale_fatture) || 0,
        totale_cassa_in_cloud: parseFloat(formData.totale_cassa_in_cloud) || 0,
      };

      if (isEditing) {
        await chiusureApi.update(id, payload);
        toast.success('Chiusura aggiornata!');
      } else if (isCompletingPartial) {
        await chiusureApi.update(completaId, payload);
        toast.success('Chiusura completata!');
      } else {
        await chiusureApi.create(payload);
        toast.success(isParziale ? 'Pranzo salvato! Ricorda di completare la chiusura stasera.' : 'Chiusura salvata!');
      }
      navigate(isParziale ? '/' : '/chiusure');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold font-heading tracking-tight">
            {isCompletingPartial ? 'Completa Chiusura' : isEditing ? 'Modifica Chiusura' : 'Nuova Chiusura'}
          </h1>
        </div>
      </header>

      {/* Banner se si sta completando una chiusura parziale */}
      {isCompletingPartial && chiusuraOriginale && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Stai completando la chiusura di oggi
              </p>
              <p className="text-xs text-amber-600">
                Pranzo già registrato: €{chiusuraOriginale.incasso_totale_pranzo?.toFixed(2)} ({chiusuraOriginale.coperti_pranzo} coperti)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="px-4 py-6 max-w-lg mx-auto space-y-6">
        {/* Date Selection */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      data-testid="date-picker-btn"
                      className="h-14 px-4 justify-start text-left font-normal"
                    >
                      <CalendarDays className="w-5 h-5 mr-3 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Data Servizio</p>
                        <p className="font-semibold">
                          {format(date, 'd MMMM yyyy', { locale: it })}
                        </p>
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      initialFocus
                      locale={it}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div 
                className="px-4 py-2 bg-secondary rounded-lg text-center"
                data-testid="giorno-settimana"
              >
                <p className="text-xs text-muted-foreground">Giorno</p>
                <p className="font-bold font-mono-numbers text-lg">{giornoSettimana}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Pranzo/Cena/Fiscale */}
        <Tabs defaultValue="pranzo" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-14">
            <TabsTrigger 
              value="pranzo" 
              data-testid="tab-pranzo"
              className="h-12 text-sm gap-1"
            >
              <Sun className="w-4 h-4" />
              Pranzo
            </TabsTrigger>
            <TabsTrigger 
              value="cena" 
              data-testid="tab-cena"
              className="h-12 text-sm gap-1"
            >
              <Moon className="w-4 h-4" />
              Cena
            </TabsTrigger>
            <TabsTrigger 
              value="fiscale" 
              data-testid="tab-fiscale"
              className="h-12 text-sm gap-1"
            >
              <Receipt className="w-4 h-4" />
              Fiscale
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="pranzo" className="mt-4 space-y-4 animate-fade-in">
            {/* Checkbox Chiuso Pranzo */}
            <Card className={chiusoPranzo ? "bg-muted/50 border-dashed" : ""}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <XCircle className={cn("w-5 h-5", chiusoPranzo ? "text-red-500" : "text-muted-foreground")} />
                    <div>
                      <p className="font-medium">Chiuso a pranzo</p>
                      <p className="text-xs text-muted-foreground">Attiva se il locale era chiuso</p>
                    </div>
                  </div>
                  <Switch
                    checked={chiusoPranzo}
                    onCheckedChange={handleChiusoPranzo}
                    data-testid="switch-chiuso-pranzo"
                  />
                </div>
              </CardContent>
            </Card>

            {!chiusoPranzo && (
              <>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base section-header">Coperti</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <NumberInput 
                      icon={Users} 
                      label="Numero coperti" 
                      field="coperti_pranzo"
                      value={formData.coperti_pranzo}
                      onChange={handleInputChange}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base section-header">Incassi</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <NumberInput 
                      icon={Banknote} 
                      label="Contanti" 
                      field="contanti_pranzo"
                      isCurrency
                      value={formData.contanti_pranzo}
                      onChange={handleInputChange}
                    />
                    <NumberInput 
                      icon={CreditCard} 
                      label="POS" 
                      field="pos_pranzo"
                      isCurrency
                      value={formData.pos_pranzo}
                      onChange={handleInputChange}
                    />
                    <NumberInput 
                      icon={Building2} 
                      label="Bonifico" 
                      field="bonifico_pranzo"
                      isCurrency
                      value={formData.bonifico_pranzo}
                      onChange={handleInputChange}
                    />
                    <NumberInput 
                      icon={FileText} 
                      label="Assegno" 
                      field="assegno_pranzo"
                      isCurrency
                      value={formData.assegno_pranzo}
                      onChange={handleInputChange}
                    />
                    <NumberInput 
                      icon={Ticket} 
                      label="Buoni pasto" 
                      field="buoni_pasto_pranzo"
                      isCurrency
                      value={formData.buoni_pasto_pranzo}
                      onChange={handleInputChange}
                    />
                  </CardContent>
                </Card>

                {/* Riepilogo Totale Pranzo */}
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-green-800">Totale Incasso Pranzo</span>
                      </div>
                      <span className="text-2xl font-bold font-mono text-green-700">
                        € {(
                          (parseFloat(formData.contanti_pranzo) || 0) +
                          (parseFloat(formData.pos_pranzo) || 0) +
                          (parseFloat(formData.bonifico_pranzo) || 0) +
                          (parseFloat(formData.assegno_pranzo) || 0) +
                          (parseFloat(formData.buoni_pasto_pranzo) || 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base section-header">Note</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      data-testid="input-note_pranzo"
                      placeholder="Note opzionali..."
                      value={formData.note_pranzo}
                      onChange={(e) => handleInputChange('note_pranzo', e.target.value)}
                      className="min-h-[100px]"
                    />
                  </CardContent>
                </Card>
              </>
            )}

            {chiusoPranzo && (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <XCircle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">Locale chiuso a pranzo</p>
                  <p className="text-xs text-muted-foreground">Tutti i valori sono stati impostati a zero</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="cena" className="mt-4 space-y-4 animate-fade-in">
            {/* Checkbox Chiuso Cena */}
            <Card className={chiusoCena ? "bg-muted/50 border-dashed" : ""}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <XCircle className={cn("w-5 h-5", chiusoCena ? "text-red-500" : "text-muted-foreground")} />
                    <div>
                      <p className="font-medium">Chiuso a cena</p>
                      <p className="text-xs text-muted-foreground">Attiva se il locale era chiuso</p>
                    </div>
                  </div>
                  <Switch
                    checked={chiusoCena}
                    onCheckedChange={handleChiusoCena}
                    data-testid="switch-chiuso-cena"
                  />
                </div>
              </CardContent>
            </Card>

            {!chiusoCena && (
              <>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base section-header">Coperti</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <NumberInput 
                      icon={Users} 
                      label="Numero coperti" 
                      field="coperti_cena"
                      value={formData.coperti_cena}
                      onChange={handleInputChange}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base section-header">Incassi</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <NumberInput 
                      icon={Banknote} 
                      label="Contanti" 
                      field="contanti_cena"
                      isCurrency
                      value={formData.contanti_cena}
                      onChange={handleInputChange}
                    />
                    <NumberInput 
                      icon={CreditCard} 
                      label="POS" 
                      field="pos_cena"
                      isCurrency
                      value={formData.pos_cena}
                      onChange={handleInputChange}
                    />
                    <NumberInput 
                      icon={Building2} 
                      label="Bonifico" 
                      field="bonifico_cena"
                      isCurrency
                      value={formData.bonifico_cena}
                      onChange={handleInputChange}
                    />
                    <NumberInput 
                      icon={FileText} 
                      label="Assegno" 
                      field="assegno_cena"
                      isCurrency
                      value={formData.assegno_cena}
                      onChange={handleInputChange}
                    />
                    <NumberInput 
                      icon={Ticket} 
                      label="Buoni pasto" 
                      field="buoni_pasto_cena"
                      isCurrency
                      value={formData.buoni_pasto_cena}
                      onChange={handleInputChange}
                    />
                  </CardContent>
                </Card>

                {/* Riepilogo Totale Cena */}
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-green-800">Totale Incasso Cena</span>
                      </div>
                      <span className="text-2xl font-bold font-mono text-green-700">
                        € {(
                          (parseFloat(formData.contanti_cena) || 0) +
                          (parseFloat(formData.pos_cena) || 0) +
                          (parseFloat(formData.bonifico_cena) || 0) +
                          (parseFloat(formData.assegno_cena) || 0) +
                          (parseFloat(formData.buoni_pasto_cena) || 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base section-header">Note</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      data-testid="input-note_cena"
                      placeholder="Note opzionali..."
                      value={formData.note_cena}
                      onChange={(e) => handleInputChange('note_cena', e.target.value)}
                      className="min-h-[100px]"
                    />
                  </CardContent>
                </Card>
              </>
            )}

            {chiusoCena && (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <XCircle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">Locale chiuso a cena</p>
                  <p className="text-xs text-muted-foreground">Tutti i valori sono stati impostati a zero</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="fiscale" className="mt-4 space-y-4 animate-fade-in">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base section-header flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-primary" />
                  Riepilogo Fiscale
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <NumberInput 
                  icon={Receipt} 
                  label="Totale Scontrini" 
                  field="totale_scontrini"
                  isCurrency
                  value={formData.totale_scontrini}
                  onChange={handleInputChange}
                />
                <NumberInput 
                  icon={FileText} 
                  label="Totale Fatture" 
                  field="totale_fatture"
                  isCurrency
                  value={formData.totale_fatture}
                  onChange={handleInputChange}
                />
                <NumberInput 
                  icon={Cloud} 
                  label="Totale Cassa in Cloud" 
                  field="totale_cassa_in_cloud"
                  isCurrency
                  value={formData.totale_cassa_in_cloud}
                  onChange={handleInputChange}
                />
              </CardContent>
            </Card>

            {/* Calculated Fields Preview */}
            <Card className="bg-secondary/50 border-dashed">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-muted-foreground" />
                  Valori Calcolati (anteprima)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <span className="text-sm text-muted-foreground">Incasso Giorno</span>
                  <span className="font-mono-numbers font-semibold text-green-600">
                    {formatCurrency(calculatedFields.incasso_giorno)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <span className="text-sm text-muted-foreground">Proforma</span>
                  <span className="font-mono-numbers font-semibold">
                    {formatCurrency(calculatedFields.proforma)}
                  </span>
                </div>
                <div className={cn(
                  "flex items-center justify-between p-3 rounded-lg",
                  calculatedFields.delta_cassa_cloud_vs_incasso !== 0 
                    ? "bg-amber-50 border border-amber-200" 
                    : "bg-white"
                )}>
                  <span className="text-sm text-muted-foreground">Delta Cloud vs Incasso</span>
                  <span className={cn(
                    "font-mono-numbers font-semibold",
                    calculatedFields.delta_cassa_cloud_vs_incasso !== 0 
                      ? "text-amber-600" 
                      : "text-green-600"
                  )}>
                    {calculatedFields.delta_cassa_cloud_vs_incasso > 0 ? '+' : ''}
                    {formatCurrency(calculatedFields.delta_cassa_cloud_vs_incasso)}
                  </span>
                </div>
                {calculatedFields.delta_cassa_cloud_vs_incasso !== 0 && (
                  <p className="text-xs text-amber-600 text-center">
                    ⚠️ C'è una differenza tra Cassa Cloud e incasso effettivo
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Buttons */}
        <div className="space-y-3">
          {/* Pulsante principale: Salva Chiusura Completa */}
          <Button 
            data-testid="save-btn"
            onClick={() => handleSubmit(false)}
            disabled={saving}
            className="w-full h-16 text-lg font-bold uppercase tracking-wider rounded-xl shadow-lg"
          >
            {saving ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="w-6 h-6 mr-2" />
                {isCompletingPartial ? 'Completa Chiusura' : isEditing ? 'Aggiorna Chiusura' : 'Salva Chiusura Completa'}
              </>
            )}
          </Button>
          
          {/* Pulsante secondario: Salva solo Pranzo (visibile solo in creazione) */}
          {!isEditing && !isCompletingPartial && (
            <Button 
              data-testid="save-partial-btn"
              variant="outline"
              onClick={() => handleSubmit(true)}
              disabled={saving}
              className="w-full h-14 text-base font-semibold rounded-xl border-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Clock className="w-5 h-5 mr-2" />
                  Salva solo Pranzo (completa stasera)
                </>
              )}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default NuovaChiusuraPage;
